'use client';
import { Button } from '@ui';
import Papa from 'papaparse';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputErrorMessage } from '../InputErrorMessage';
import { SelectInput } from '../SelectInput';
import FormInput from './FormInput';

export interface Field {
    label: string;
    name: string;
    autoMappingKeys?: string[];
    type?: string;
    rows?: string[];
    columns?: string[];
}

interface Props {
    namePrefix?: string;
    mapToFields?: Field[];
    forceHeaderRow?: boolean;
}

const FormCSVImport = ({ mapToFields = [], namePrefix = 'mapping', forceHeaderRow }: Props) => {
    const { t } = useTranslation('form');
    const { register, watch, setValue } = useFormContext();
    const [csvHeadings, setCsvHeadings] = useState<string[]>([]);
    const useHeaderRow = watch('use_header_row');
    const files = watch('csv');

    const handleCSVParse = (files: FileList) => {
        if (files?.length > 0) {
            const reader = new FileReader();
            reader.onload = () => {
                const parseResult = Papa.parse(String(reader.result));
                setCsvHeadings(parseResult.data[0] as string[]);
                setValue('csv_separator', parseResult.meta.delimiter ?? ';');
            };
            reader.readAsText(files[0]);
        }
    };

    useEffect(() => {
        if (forceHeaderRow) {
            setValue('use_header_row', true);
        }

        setValue(
            namePrefix,
            mapToFields.reduce((carry, field) => {
                const mappedIndex = csvHeadings.findIndex((r) => {
                    const columnHeader = r?.toLowerCase().trim();

                    return (
                        columnHeader === field.label?.toLowerCase() ||
                        field.autoMappingKeys?.some((k) => k.toLowerCase() === columnHeader)
                    );
                });
                return {
                    ...carry,
                    [field.name]: mappedIndex !== -1 ? mappedIndex : undefined
                };
            }, {})
        );
    }, [csvHeadings, namePrefix, mapToFields, useHeaderRow, forceHeaderRow]);

    useEffect(() => handleCSVParse(files), [files]);
    const inputFileRef = useRef<HTMLInputElement>(undefined);
    const { ref: formRef, ...inputFields } = register('csv');

    return (
        <>
            <input
                {...inputFields}
                ref={(ref) => {
                    formRef(ref);
                    inputFileRef.current = ref;
                }}
                type="file"
                hidden
            />
            <Button className="tw-mb-4 tw-w-full" onPress={() => inputFileRef.current.click()}>
                {files?.[0]?.name || t('csvImport.selectFile')}
            </Button>
            {files?.length > 0 && (
                <>
                    <hr />
                    <FormInput.Checkbox
                        name="use_header_row"
                        label={`Erste Zeile ist Titelzeile ${forceHeaderRow ? '(erzwungen)' : ''}`}
                        disabled={forceHeaderRow}
                    />
                    <SelectInput
                        name="csv_separator"
                        label="CSV Unterteiler"
                        startsEmpty={false}
                        optionsWithKey={[';', ',', ':', '\t'].map((s) => ({
                            label: s === '\t' ? 'Tabulator' : s,
                            value: s
                        }))}
                    />
                    <hr />
                    {mapToFields.map((field) =>
                        field?.type === 'table' ? (
                            <TableField
                                label={field.label}
                                name={namePrefix + '.' + field.name}
                                key={field.name}
                                field={field}
                                options={csvHeadings.map((h, i) => ({
                                    label: useHeaderRow ? h : `Spalte ${i}`,
                                    value: i
                                }))}
                            />
                        ) : (
                            <MappingField
                                label={field.label}
                                name={namePrefix + '.' + field.name}
                                key={field.name}
                                options={csvHeadings.map((h, i) => ({
                                    label: useHeaderRow ? h : `Spalte ${i}`,
                                    value: i
                                }))}
                            />
                        )
                    )}
                </>
            )}
        </>
    );
};

const TableField = ({
    label,
    name,
    field,
    options = []
}: {
    label: string;
    name: string;
    field: any;
    options: { label: any; value: any }[];
}) => {
    const { register } = useFormContext();
    const days = field.columns;

    return (
        <>
            {days.map((day, dayIndex) => (
                <div key={`${name}_${day.toLowerCase()}`} className="input-group my-2">
                    <div className="input-group-prepend w-50">
                        <span className="input-group-text w-100">
                            {label} ({day})
                        </span>
                    </div>
                    <select {...register(`${name}.[${dayIndex}]`)} className="form-control">
                        <option key="empty" value="">
                            Bitte wählen ...
                        </option>
                        {options.map((o) => (
                            <option key={`${name}_option_${o.label || o.value}`} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
            ))}
            <InputErrorMessage name={name} />
        </>
    );
};

const MappingField = ({
    label,
    name,
    options = []
}: {
    label: string;
    name: string;
    options: { label: any; value: any }[];
}) => {
    const { register } = useFormContext();

    return (
        <>
            <div className="input-group my-2">
                <div className="input-group-prepend w-50">
                    <span className="input-group-text w-100">{label}</span>
                </div>
                <select {...register(name)} className="form-control">
                    <option key="empty" value="">
                        Bitte wählen ...
                    </option>
                    {options.map((o) => (
                        <option key={`${name}_option_${o.label || o.value}`} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>
            <InputErrorMessage name={name} />
        </>
    );
};

export default FormCSVImport;
